import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { z } from "zod";
import { usePasswordReset } from "@/api/auth";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Field,
	FieldError,
	FieldGroup,
	FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";

const schema = z.object({
	email: z.string().email(),
});
type FormValues = z.infer<typeof schema>;

export function ForgotPasswordPage() {
	const { t } = useTranslation();
	const { lang } = useParams<{ lang: string }>();
	const [sent, setSent] = useState(false);
	const reset = usePasswordReset();

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<FormValues>({ resolver: zodResolver(schema) });

	function onSubmit(values: FormValues) {
		reset.mutate(values, {
			onSuccess: () => setSent(true),
			onError: () => setSent(true),
		});
	}

	return (
		<div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10">
			<div className="w-full max-w-sm">
				<Card>
					<CardHeader>
						<CardTitle>{t("forgotPassword.title")}</CardTitle>
						<CardDescription>{t("forgotPassword.description")}</CardDescription>
					</CardHeader>
					<CardContent>
						{sent ? (
							<div className="flex flex-col gap-4">
								<p className="text-sm text-muted-foreground">
									{t("forgotPassword.successMessage")}
								</p>
								<Button variant="outline" asChild>
									<Link to={`/${lang ?? "pl"}/login`}>
										{t("forgotPassword.backToLogin")}
									</Link>
								</Button>
							</div>
						) : (
							<form
								onSubmit={handleSubmit(onSubmit)}
								className="flex flex-col gap-4"
							>
								<FieldGroup>
									<Field>
										<FieldLabel htmlFor="email">
											{t("forgotPassword.emailLabel")}
										</FieldLabel>
										<Input
											id="email"
											type="email"
											autoComplete="email"
											spellCheck={false}
											placeholder={t("forgotPassword.emailPlaceholder")}
											{...register("email")}
										/>
										{errors.email && (
											<FieldError>
												{t("forgotPassword.emailInvalid")}
											</FieldError>
										)}
									</Field>
								</FieldGroup>
								<Button
									type="submit"
									className="w-full"
									disabled={reset.isPending}
								>
									{reset.isPending
										? t("forgotPassword.sending")
										: t("forgotPassword.submit")}
								</Button>
								<Button variant="ghost" asChild>
									<Link to={`/${lang ?? "pl"}/login`}>
										{t("forgotPassword.backToLogin")}
									</Link>
								</Button>
							</form>
						)}
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
